import React, { useRef, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import JitsiSession from "./JitsiSession";
import { SESSION_TYPES } from "./sessionTypes";
import EkycDock from "../eKYC/EkycDock";
import { APIConstant } from "services/api-constant";
import styles from "../eKYC/eKYC.module.scss";
import query from "services/api/api-service";
import FileUpload from "components/minor/file-upload/file-upload";
import FormDialog from "components/minor/dialog/form-dialog";
import Images from "assets/Images";

const useQuery = () => new URLSearchParams(useLocation().search);

const uploadFile = [
  { message: "I couldn't detect your ID card clearly from the video. Please upload a photo of your ID to continue.", action: "upload id card" },
  { message: "Your face didn’t match the photo on your ID. Let’s try again. Please look into the camera clearly.", action: "face mismatch" },
  { message: "Hmm, I couldn’t confirm you're live. Please follow the instructions carefully this time.", action: "liveness failed" },
]

// eKYC type — lifted from eKYC/StartEKYCSession.jsx unchanged, except:
//  - the Jitsi mount now uses the shared JitsiSession wrapper (same
//    captureScreenshot/getApi/getLocalVideoStream ref API the avatar-step
//    engine below depends on), configured from sessionTypes.js's "ekyc" entry.
//  - room/session are now actually read from the URL (ekycSession.jsx has
//    generated them all along — /eKYC?room=&session= — but the page never
//    read them, always using a hardcoded room; that's fixed here).
const EkycBody = () => {
  const urlQuery = useQuery();
  const roomName = urlQuery.get("room") || "ekyc-robot";

  const jitsiRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [result, setResult] = useState({});
  const [idImage, setIdImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [talking, setTalking] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [otpPopUp, setOtpPopUp] = useState(false);
  const [idCardBase64, setIdCardBase64] = useState(null);
  const [meetingStarted, setMeetingStarted] = useState(false);
  const [attempts, setAttempts] = useState(1);
  const [initialImage, setInitialImage] = useState(null);
  const [livenessPassed, setLivenessPassed] = useState();
  const steps = [
    { id: 1, name: "Capture ID Image", initialMessage: "Capturing ID image...", action: "captureID", successMessage: "ID image Captured successfully", failureMessage: "I couldn't detect your ID card clearly. Please upload a photo of your ID card", completed: false },
    { id: 2, name: "Face Match", initialMessage: "Please wait, we are matching your face with the ID", action: "faceMatch", successMessage: "Face matched successfully", failureMessage: "your face didn’t match the photo on your ID. Let’s try again", completed: false },
    { id: 3, name: "Liveness Check", initialMessage: "Let's check the livenes now... Please blink your eyes and rotate hour head left to right and righ to left", action: "liveness", successMessage: "Liveness check is completed", failureMessage: "Hmm, I couldn’t confirm you're live. Please follow the instructions carefully this time.", completed: false },
    { id: 4, name: "OTP", initialMessage: "We almost completed, let's do this last OTP verification...", successMessage: "Congratz the OTP verification is completed", action: "ocr", failureMessage: "The OTP verification is failed, let's try again...", completed: false },
    { id: 5, name: "Confirmation", initialMessage: "All done! Here's what I found:", successMessage: "congratz the EKYC process is completed... Thanks for joining...", action: "summary", failureMessage: "the EKYC is pending and the executive will call you and confirm the verification manually...", completed: false }
  ];

  const startRecordingIframe = async (attempt) => {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { frameRate: 30 },
    audio: false,
  });

  const recorder = new MediaRecorder(stream);
  const chunks = [];

  recorder.ondataavailable = e => chunks.push(e.data);
  recorder.onstop = () => {
    const blob = new Blob(chunks, { type: 'video/mp4' });
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Video = reader.result;
      nextStepAction(base64Video, attempt);
    };
    reader.readAsDataURL(blob);
  };
  recorder.start();
  setTimeout(() => {
    recorder.stop();
  }, 10000);
  };

  const nextStepAction = async (base64, attempt=1) => {
    if (currentStep === 0) {
      setShowFileUpload(true);
    } else if (currentStep === 1) {
      const payload = {
        id_image: idCardBase64,
        live_image: attempts === 1 ? initialImage : base64,
      };
      const res = await query(APIConstant.faceMatch, true, payload, 'POST');
      if (res?.verified) {
        speak(steps[currentStep].successMessage);
        setTimeout(() => setCurrentStep((prev) => prev + 1), 2000);
      } else {
          setAttempts((prev) => prev + 1);
          speak(steps[currentStep].failureMessage);
          setTimeout(() => setCurrentStep((prev) => prev - 1), 2000);
          setShowFileUpload(true);
      }
    } else if (currentStep === 2) {
      const payload = {
        video_base64: base64,
      };
      const res = await query(APIConstant.livenessVideo, true, payload, 'POST');
      if (res?.liveness_passed) {
        speak(steps[currentStep].successMessage);
        setTimeout(() => setCurrentStep((prev) => prev + 1), 2000);
        setLivenessPassed(true);
      } else if (attempt>=2) {
        speak(steps[currentStep].failureMessage);
        setLivenessPassed(false);
        setTimeout(() => setCurrentStep((prev) => prev + 1), 2000);
      } else {
        speak(steps[currentStep].failureMessage);
        setTimeout(() => speak(steps[currentStep].initialMessage), 2000);
        setTimeout(() => takeScreenshot(attempt+1), 5000);
      }
    } else if (currentStep === 3) {
      setOtpPopUp(true);
    }
  }

const takeScreenshot = async (attempt, intial) => {
    if (jitsiRef.current) {
    const api = jitsiRef.current.getApi();
      if (intial) {
        api.captureLargeVideoScreenshot().then(data => {
        setInitialImage(data.dataURL);
        nextStepAction();
      });
      } else if (currentStep === 2) {
        startRecordingIframe(attempt);
      } else {
        api.captureLargeVideoScreenshot().then(data => {
        nextStepAction(data.dataURL);
      });
      }
    }
  };

  const speak = (text) => {
    const msg = new SpeechSynthesisUtterance(text);
    msg.lang = "en-US";
    window.speechSynthesis.speak(msg);
    setTalking(true);
    msg.onend = () => {
      setTalking(false);
    }
  };

  const nextStep = async () => {
    const step = steps[currentStep];
    speak(step.message);
    if (step.action === "wait") {
      await new Promise((res) => setTimeout(res, 3000));
    }
  };

  useEffect(() => {
    if (meetingStarted) {
      setCurrentStep(0);
      const step = steps[currentStep];
      speak(step.initialMessage);
      setTimeout(() => takeScreenshot(0, true), 2000);
    }
  }, [meetingStarted]);

  useEffect(() => {
    if (meetingStarted) {
      const step = steps[currentStep];
      speak(step.initialMessage);
      if (currentStep !== 3) setTimeout(() => takeScreenshot(), 10000);
      else if (currentStep === 3) nextStepAction();
      else if (currentStep === 4) {
        speak(step.successMessage);
      }
    }
  }, [currentStep]);

  const onHandleFiles = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onloadend = async () => {
    const base64 = reader.result;
    const payload = {
      image_base64: base64,
    };
    const res = await query(APIConstant.detectIdCard, true, payload, 'POST');
    if (res?.id_card_detected) {
      speak(steps[currentStep].successMessage);
      setIdCardBase64(base64);
      setCurrentStep((prev) => prev + 1);
      setShowFileUpload(false);
    } else {
      speak(steps[currentStep].failureMessage);
    }
  };
  reader.readAsDataURL(file);
};

  const confirmOtp = async () => {
    setOtpPopUp(false);
    speak(steps[currentStep].successMessage);
    setCurrentStep((prev) => prev + 1);
  }

  const handleClose = () => {
    setShowFileUpload(false);
    setOtpPopUp(false);
  };

  const jitsi = SESSION_TYPES.ekyc.jitsi;

  return (
    <div className={styles.ekycContainer}>
        <div className={styles.videoWrapper}>
          <div className={styles.currentStep}>
            <span style={{color: 'black'}}>{steps[currentStep].message}</span>
          </div>
          <div className={styles.videoChatWindow}>
            <JitsiSession
              ref={jitsiRef}
              roomName={roomName}
              userInfo={{ displayName: "Customer" }}
              scriptSrc={jitsi.scriptSrc}
              configOverwrite={jitsi.configOverwrite}
              interfaceConfigOverwrite={jitsi.interfaceConfigOverwrite}
              onJoined={() => setMeetingStarted(true)}
            />
            { meetingStarted && (
            <div className={styles.stepperContainer}>
              <ul>
                <li className={`${currentStep === 0 && styles.active} ${styles.completed_success} `}>
                  { currentStep === 0
                    ? <div className={styles.activeWrapper}>
                        <span>
                          { talking
                            ? <img src={Images.AVATAR_SPEAKING} alt="Avatar Speaking" width="80"/>
                            : <img src={Images.AVATAR_MUTE} alt="Avatar Mute" width="80"/>
                          }
                        </span>
                        <div className={styles.backgroundAnimate}><img src={Images.BG_ANIMATE} alt="Background Animation" width={150}/></div>
                      </div>
                    : <span className={styles.inactiveStep}>1</span>
                  }
                  <p>Upload Id Image</p>
                </li>
                <li className={` ${currentStep === 1 && styles.active} ${styles.completed_failed} `}>
                  { currentStep === 1
                  ? <div className={styles.activeWrapper}>
                        <span>
                          { talking
                            ? <img src={Images.AVATAR_SPEAKING} alt="Avatar Speaking" width="80"/>
                            : <img src={Images.AVATAR_MUTE} alt="Avatar Mute" width="80"/>
                          }
                        </span>
                        <div className={styles.backgroundAnimate}><img src={Images.BG_ANIMATE} alt="Background Animation" width={150}/></div>
                    </div>
                  : <span className={styles.inactiveStep}>2</span>
                  }

                  <p>Face Match</p>
                </li>
                <li className={ currentStep === 2 && styles.active }>
                  { currentStep === 2
                    ? <div className={styles.activeWrapper}>
                          <span>
                            { talking
                              ? <img src={Images.AVATAR_SPEAKING} alt="Avatar Speaking" width="80"/>
                              : <img src={Images.AVATAR_MUTE} alt="Avatar Mute" width="80"/>
                            }
                          </span>
                          <div className={styles.backgroundAnimate}><img src={Images.BG_ANIMATE} alt="Background Animation" width={150}/></div>
                      </div>
                    : <span className={styles.inactiveStep}>3</span>
                  }
                  <p>Liveness</p>
                </li>
                <li className={ currentStep === 3 && styles.active }>
                  { currentStep === 3
                    ?  <div className={styles.activeWrapper}>
                          <span>
                            { talking
                              ? <img src={Images.AVATAR_SPEAKING} alt="Avatar Speaking" width="80"/>
                              : <img src={Images.AVATAR_MUTE} alt="Avatar Mute" width="80"/>
                            }
                          </span>
                          <div className={styles.backgroundAnimate}><img src={Images.BG_ANIMATE} alt="Background Animation" width={150}/></div>
                      </div>
                    : <span className={styles.inactiveStep}>4</span>
                  }
                  <p>OTP</p>
                </li>
                <li className={ currentStep === 4 && styles.active }>
                  { currentStep === 4
                    ?  <div className={styles.activeWrapper}>
                          <span>
                            { talking
                              ? <img src={Images.AVATAR_SPEAKING} alt="Avatar Speaking" width="80"/>
                              : <img src={Images.AVATAR_MUTE} alt="Avatar Mute" width="80"/>
                            }
                          </span>
                          <div className={styles.backgroundAnimate}><img src={Images.BG_ANIMATE} alt="Background Animation" width={150}/></div>
                      </div>
                    : <span className={styles.inactiveStep}>5</span>
                  }
                  <p>Confirmation</p>
                </li>
              </ul>
            </div>
            )}
          </div>
        </div>

        {meetingStarted && (
          <EkycDock steps={steps} currentStep={currentStep} result={result} talking={talking} />
        )}

      { showFileUpload && (
        <FormDialog
        open={showFileUpload}
        title={"Upload ID Image"}
        enableButton={false}
        padding={false}
        handleClose={handleClose}
      >
        <div className={styles.dialogCont}>
          <section className={styles.formContainer}>
            <p>Please upload an image of your photo ID, preferably a Aadhar card</p>
            <FileUpload handleChange={(e) => onHandleFiles(e)}/>
          </section>
        </div>
      </FormDialog>
      )}

      { otpPopUp && (
        <FormDialog
        open={otpPopUp}
        title={"Enter OTP"}
        enableButton={false}
        padding={false}
        handleClose={handleClose}
      >
        <div className={styles.dialogCont}>
          <button className="btn btn-primary" onClick={confirmOtp}>
            Confirm OTP
          </button>
        </div>
      </FormDialog>
      )}

      {/* Results */}
      {currentStep === steps.length - 1 && result && (
        <div className="mt-6 bg-gray-800 p-4 rounded shadow space-y-2">
          <p><strong>Face Match:</strong> {result.faceMatch?.verified ? "✅ Match" : "❌ No Match"}</p>
          <p><strong>Confidence:</strong> {result.faceMatch?.score?.toFixed(2)}%</p>
          <p><strong>Liveness:</strong> {result.liveness?.liveness ? "✅ Passed" : "❌ Failed"}</p>
          <p><strong>Head Turn:</strong> {result.liveness?.head_turn}</p>
          <p><strong>Blink Detected:</strong> {result.liveness?.blink ? "Yes" : "No"}</p>
          <div className="mt-2">
            <strong>ID OCR:</strong>
            <pre className="bg-gray-700 p-3 mt-2 rounded text-sm whitespace-pre-wrap">
              {result.ocr?.ocr_text}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default EkycBody;
